import React, { useState, useEffect, useRef } from 'react';
import { Pause, Play } from 'lucide-react';

const ContinuousCardCarousel = () => {
  const [isPaused, setIsPaused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const containerRef = useRef(null);
  
  const cards = [
    {
      id: 1,
      image: "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=400&h=300&q=80",
      title: "Flexible Learning Paths", 
      description: "Learn at your own pace, anytime, anywhere on our platform."
    },
    {
      id: 2,
      image: "https://images.unsplash.com/photo-1517048676732-d65bc937f952?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=400&h=300&q=80",
      title: "Expert-Led Courses",
      description: "Gain insights from industry professionals and experienced instructors."
    },
    {
      id: 3,
      image: "https://images.unsplash.com/photo-1531482615713-2afd69097998?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=400&h=300&q=80",
      title: "Hands-On Projects",
      description: "Build a strong portfolio with real-world coding assignments."
    },
    {
      id: 4,
      image: "https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=400&h=300&q=80",
      title: "Career Services",
      description: "Get job-ready with resume workshops and interview preparation."
    },
    {
      id: 5,
      image: "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=400&h=300&q=80",
      title: "Vibrant Community",
      description: "Connect with peers, mentors, and alumni in our active network."
    }
  ];

  // Double the cards to create seamless loop
  const duplicatedCards = [...cards, ...cards];

  useEffect(() => {
    if (isPaused || isHovered) return;

    const container = containerRef.current;
    let animationFrameId;
    let startTime;
    const duration = 30000; // Time to scroll through all cards once

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = ((timestamp - startTime) % duration) / duration;
      
      if (container) {
        const totalWidth = container.scrollWidth / 2;
        container.scrollLeft = progress * totalWidth;
        
        // Reset scroll position when we reach the duplicate set
        if (container.scrollLeft >= totalWidth) {
          startTime = timestamp;
          container.scrollLeft = 0;
        }
      }
      
      animationFrameId = requestAnimationFrame(animate);
    };

    animationFrameId = requestAnimationFrame(animate);

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [isPaused, isHovered]);

  return (
    <div className="relative w-full max-w-7xl mx-auto bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-8 md:p-12 mt-10 overflow-hidden shadow-2xl">
      <div className="text-center mb-8 md:mb-12">
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">
          Experience the Swiss Tech Difference
        </h2>
        <p className="text-lg text-gray-300">
          A glimpse into our dynamic learning environment and community.
        </p>
      </div>
      <div className="absolute top-4 right-4 z-20">
        <button
          onClick={() => setIsPaused(!isPaused)}
          className="bg-white/10 hover:bg-white/20 text-white p-2.5 rounded-full backdrop-blur-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-white/50"
          aria-label={isPaused ? "Resume animation" : "Pause animation"}
        >
          {isPaused ? (
            <Play className="w-4 h-4" />
          ) : (
            <Pause className="w-4 h-4" />
          )}
        </button>
      </div>

      <div
        ref={containerRef}
        className="flex gap-6 md:gap-8 overflow-x-hidden"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {duplicatedCards.map((card, index) => (
          <div
            key={`${card.id}-${index}`}
            className="flex-none w-72 md:w-80 group"
          >
            <div className="relative h-full bg-gray-800 rounded-xl overflow-hidden shadow-xl transform transition-all duration-300 group-hover:scale-105 group-hover:shadow-2xl group-hover:-translate-y-1">
              <img
                src={card.image}
                alt={card.title}
                className="w-full h-48 object-cover transition-transform duration-300 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
              
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <h3 className="text-white text-xl font-semibold mb-2 group-hover:text-sky-400 transition-colors duration-300">
                  {card.title}
                </h3>
                <p className="text-gray-300 text-sm leading-relaxed">
                  {card.description}
                </p>
              </div>

              <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-50 transition-opacity duration-300" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ContinuousCardCarousel;